import { ReactNode, useMemo } from "react";
import { useIngredientColumns } from "./useIngredientColumns";
import { useGetAllIngredient } from "src/api/client-api/ingredients/useGetAllIngredients";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useDelayedSearch } from "../../../utils/useDelayedSearch";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useIngredientMenuItems } from "./IngredientsGrid/useIngredientMenuItems";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { IngredientListContext, IngredientListContextInitialValues } from "./useIngredientListContext";
import type { ComponentAppearance } from "src/components/appearance";
import { useListExportAction } from "src/components/Grid/headers/useListExportAction";
import { useScoreRecalculationAction } from "src/components/Grid/headers/useScoreRecalculationAction";
import { ingredientGridKey } from "src/api/client-api/ingredients/useGetAllIngredients";

// `useIngredientListContext` and the underlying `IngredientListContext` live in
// `./useIngredientListContext.ts` so this file only exports a component, which
// is required for Vite React Fast Refresh to hot-swap this module without
// remounting the whole subtree (and breaking the React Context identity).

type IngredientListContextProviderProps = { appearance?: ComponentAppearance; children: ReactNode };
export const IngredientListContextProvider = (props: IngredientListContextProviderProps) => {
    const { appearance = "legacy", children } = props;

    const { canUser, currentCampaign } = useFoodpilotContext();
    const { company } = useCompanyMetadata();

    const { allAsAxiosParams, filters, sort, pagination } = useUrlToApi({ page: "1", itemsPerPage: "20" });

    const globalSearch = useDelayedSearch();

    const axiosParams = useMemo(() => {
        return {
            ...allAsAxiosParams,
        };
    }, [allAsAxiosParams]);

    const { ingredients, appSearch, totalItems, isRefetching, isReady } = useGetAllIngredient({
        cacheTime: 0,
        search: globalSearch.delayedSearch,
        campaignId: currentCampaign?.id,
        companyId: company.id,
        params: axiosParams,
        scoresQuery: {
            scores: {
                subscores: false,
            },
        },
    });

    const gridKey = "ingredients-grid";

    const { mrt_ingredientColumns } = useIngredientColumns(ingredients);
    const columnConfiguration = useGridColumns(mrt_ingredientColumns, gridKey);

    const menuItems = useIngredientMenuItems({
        appearance,
        canUser,
        companyIds: {
            "@id": company["@id"],
            id: company.id,
        },
    });

    const exportAction = useListExportAction({
        path: "/api/ingredients",
        filters,
        sort,
        search: globalSearch.delayedSearch,
        campaignId: currentCampaign?.id,
        organizationId: company.id,
        fileNameStem: "ingredients",
    });

    const scoreRecalculationAction = useScoreRecalculationAction({
        path: "/api/ingredients",
        entityKind: "ingredient",
        invalidatedQueryKey: ingredientGridKey,
        pageEntities: (ingredients ?? []).map(({ id, name }) => ({ id, name })),
        campaignId: currentCampaign?.id,
        organizationId: company.id,
    });

    const items: IngredientListContextInitialValues = {
        mrt_columns: mrt_ingredientColumns,
        columnConfiguration,
        appSearch,
        pagination,
        totalItems,
        ingredients: ingredients ?? [],
        isReady,
        isRefetching,
        menuItems,
        tableActions: [exportAction, scoreRecalculationAction],
        globalSearch,

        gridKey: gridKey,
    };

    return <IngredientListContext.Provider value={items}>{children}</IngredientListContext.Provider>;
};
